import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, BookOpen, ThumbsUp, ThumbsDown, Wrench, RotateCcw, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ModalCloseButton from '@/components/ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 91c+ — Ticker auto-link parser for comments.
//
// Goal: when a visitor's comment mentions a ticker symbol in
// ALL-CAPS (e.g. "JEPI's screenshot is wrong", "the SCHD example
// in 7.2 is off"), turn the symbol into a clickable chip in the
// admin stats modal so the maintainer can jump straight into that
// ticker's Stock Modal.
//
// Detection is intentionally conservative:
//   • Match 1–5 letter all-uppercase tokens, optionally suffixed
//     with .X / -X for tickers like BRK.B and BF-A.
//   • Skip a small denylist of common English ALL-CAPS words that
//     would otherwise produce false positives (I, AM, OK, etc.).
//   • Skip tokens immediately preceded by a # or $ (already a
//     hashtag/cashtag — leave the surrounding char visible).
const TICKER_DENYLIST = new Set([
  // 1-letter
  'I', 'A',
  // 2-letter common english / standalone
  'AM','AN','AS','AT','BE','BY','DO','GO','HE','IF','IN','IS','IT','ME',
  'MY','NO','OF','OH','OK','ON','OR','SO','TO','UP','US','WE',
  // 3-letter
  'ALL','AND','ARE','BUT','CAN','DID','FOR','GET','GOT','HAD','HAS','HER',
  'HIM','HIS','HOW','ITS','LET','MAY','NOR','NOT','NOW','OUR','OUT','PUT',
  'SAW','SAY','SEE','SHE','THE','TOO','TRY','TWO','USE','WAS','WAY','WHO',
  'WHY','YES','YET','YOU',
  // 4-letter
  'BEEN','BOTH','EACH','EVEN','FROM','HAVE','HERE','JUST','LIKE','MAKE',
  'MANY','MORE','MOST','MUCH','MUST','ONCE','ONLY','OVER','SOME','SUCH',
  'TAKE','THAN','THAT','THEM','THEN','THEY','THIS','VERY','WANT','WERE',
  'WHAT','WHEN','WILL','WITH','YOUR','GOOD','SURE','OKAY','YEAH','PAGE',
  'TEXT','SHOW','HIDE','OPEN','SAVE','OPEN','UPON','DOES','SEEM','ALSO',
  'CARD','TABS','TABS','PAGE','SAID','READ','SEEN','TOLD','KNOW','BACK',
  'HOME','LINK','MENU','SITE','HELP','ONES','BUGS','BUG ','NOPE','YEAH',
  // 5-letter
  'ABOUT','AFTER','AGAIN','BEING','COULD','EVERY','FIRST','GREAT','HELLO',
  'NEVER','OTHER','RIGHT','THESE','THINK','THOSE','THREE','UNDER','WHERE',
  'WHICH','WHILE','WORLD','WOULD','HOWTO','BUTON','TODAY','MAYBE',
  // DiviDrip-specific tokens we don't want to treat as tickers
  'DRIP','DIVIDRIP','HOWTO','TODO','TBD','TWAP','FAQ','NEW','OLD',
]);

const TICKER_RE = /\b([A-Z]{1,5}(?:[.\-][A-Z]{1,2})?)\b/g;

/**
 * Splits raw comment text into segments. Each segment is either:
 *   { type: 'text',   value: 'plain text segment' }
 * or
 *   { type: 'ticker', value: 'JEPI' }
 *
 * The ticker detector is forgiving — false positives are acceptable
 * because a click on a non-ticker will just open a missing Stock
 * Modal lookup, not crash anything.
 */
function parseCommentSegments(raw) {
  if (!raw || typeof raw !== 'string') return [{ type: 'text', value: raw || '' }];
  const segments = [];
  let lastIndex = 0;
  for (const match of raw.matchAll(TICKER_RE)) {
    const token = match[1];
    const start = match.index;
    const end = start + match[0].length;
    const prevChar = start > 0 ? raw[start - 1] : '';
    // Skip $TICKER / #TICKER — those are already linkable elsewhere
    // and showing two chips next to each other looks busy.
    if (prevChar === '$' || prevChar === '#') continue;
    // Denylist filter
    if (TICKER_DENYLIST.has(token)) continue;
    // Push the gap before this ticker as a text segment
    if (start > lastIndex) {
      segments.push({ type: 'text', value: raw.slice(lastIndex, start) });
    }
    segments.push({ type: 'ticker', value: token });
    lastIndex = end;
  }
  // Trailing tail
  if (lastIndex < raw.length) {
    segments.push({ type: 'text', value: raw.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: raw }];
}

const SECTION_LABELS = {
  portfolio: '1 · Portfolio',
  watchlist: '2 · Watchlist',
  browsing: '3 · Browsing Stocks',
  compare: '4 · Compare & Pin',
  calendar: '5 · Dividend Calendar',
  tools: '6 · Tool Box & 13 Tools',
  streaks: '7 · Streaks & Journal',
  navigation: '8 · Site Navigation',
  glossary: '9 · Glossary & Privacy',
};

/**
 * AdminHowToFeedbackButton — Round 91 (Feb 2026).
 *
 * Admin-only button that opens a modal showing per-section
 * thumbs-up / thumbs-down counts from the public How-To page, plus
 * the most recent 25 free-text comments (if any).
 *
 * Backend: GET /api/how-to/feedback/stats (admin-only).
 *
 * Renders nothing for non-admin users.
 */
export default function AdminHowToFeedbackButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // Round 91f — tracks which section is currently mid-flight on a
  // Mark Fixed / Reset action so we can disable the pill + show a
  // tiny spinner without freezing the whole modal.
  const [busySection, setBusySection] = useState(null);

  if (user?.role !== 'admin') return null;

  const fetchStats = async (preserveOpen = false) => {
    setLoading(true);
    if (!preserveOpen) {
      setData(null);
      setOpen(true);
    }
    try {
      const { data: resp } = await axios.get(`${API}/how-to/feedback/stats`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      toast.error('Could not load How-To feedback', {
        description: err?.response?.data?.detail || err?.message,
      });
      if (!preserveOpen) setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Round 91f — Admin presses "Mark fixed" after rewriting a section.
  // Writes a `last_fixed_at` timestamp on the server. Subsequent
  // feedback dated after that stamp is highlighted as POST-FIX.
  const markFixed = async (sid, clear = false) => {
    setBusySection(sid);
    try {
      await axios.post(
        `${API}/how-to/section/${sid}/mark-fixed`,
        { clear },
        { withCredentials: true },
      );
      toast.success(clear
        ? `Reset "fixed" stamp for ${SECTION_LABELS[sid] || sid}`
        : `Marked "${SECTION_LABELS[sid] || sid}" as fixed`);
      await fetchStats(true);
    } catch (err) {
      toast.error('Could not update fixed stamp', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusySection(null);
    }
  };

  const rows = Object.keys(SECTION_LABELS).map((sid) => {
    const stats = data?.by_section?.[sid] || { up: 0, down: 0, latest: null, last_fixed_at: null };
    const total = (stats.up || 0) + (stats.down || 0);
    const pct = total > 0 ? Math.round((stats.up / total) * 100) : null;
    return {
      sid,
      label: SECTION_LABELS[sid],
      up: stats.up || 0,
      down: stats.down || 0,
      total,
      pct,
      latest: stats.latest,
      lastFixedAt: stats.last_fixed_at || null,
    };
  });

  const grandTotal = rows.reduce((acc, r) => acc + r.total, 0);
  const grandUp = rows.reduce((acc, r) => acc + r.up, 0);
  const grandDown = rows.reduce((acc, r) => acc + r.down, 0);

  return (
    <>
      <button
        type="button"
        onClick={() => fetchStats(false)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white transition-colors"
        data-testid="admin-how-to-feedback-btn"
        title="View How-To page feedback (admin)"
      >
        <BookOpen className="w-3.5 h-3.5" />
        How-To Feedback
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
          onClick={() => setOpen(false)}
          data-testid="admin-how-to-feedback-modal"
        >
          <div
            className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl relative max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <h3 className="text-base font-bold flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-violet-500" />
                How-To Feedback
              </h3>
              <ModalCloseButton onClick={() => setOpen(false)} testId="admin-how-to-feedback-close" />
            </div>

            <div className="p-4 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading stats…
                </div>
              )}

              {!loading && data && (
                <>
                  <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
                    <span className="text-muted-foreground">Total responses:</span>
                    <span className="font-mono font-bold">{grandTotal}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <ThumbsUp className="w-3 h-3" /> {grandUp}
                    </span>
                    <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400">
                      <ThumbsDown className="w-3 h-3" /> {grandDown}
                    </span>
                  </div>

                  <table className="w-full text-sm" data-testid="admin-how-to-feedback-table">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b">
                        <th className="py-2 font-semibold">Section</th>
                        <th className="py-2 font-semibold text-right">Up</th>
                        <th className="py-2 font-semibold text-right">Down</th>
                        <th className="py-2 font-semibold text-right">Total</th>
                        <th className="py-2 font-semibold text-right">% helpful</th>
                        <th className="py-2 font-semibold text-right">Fixed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.sid} className="border-b last:border-b-0" data-testid={`admin-how-to-feedback-row-${r.sid}`}>
                          <td className="py-2 font-medium">{r.label}</td>
                          <td className="py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{r.up}</td>
                          <td className="py-2 text-right font-mono text-rose-700 dark:text-rose-400">{r.down}</td>
                          <td className="py-2 text-right font-mono">{r.total}</td>
                          <td className="py-2 text-right font-mono">
                            {r.pct == null
                              ? <span className="text-muted-foreground">—</span>
                              : <span className={r.pct >= 75 ? 'text-emerald-700 dark:text-emerald-400'
                                  : r.pct >= 50 ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-rose-700 dark:text-rose-400'}>{r.pct}%</span>}
                          </td>
                          <td className="py-2 text-right">
                            {r.lastFixedAt ? (
                              <div className="inline-flex items-center gap-1.5">
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                                  title={`Section marked fixed on ${r.lastFixedAt}`}
                                >
                                  <CheckCircle2 className="w-2.5 h-2.5" />
                                  {r.lastFixedAt.slice(0, 10)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => markFixed(r.sid, true)}
                                  disabled={busySection === r.sid}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 hover:bg-muted disabled:opacity-50"
                                  title="Reset fixed stamp"
                                  data-testid={`admin-how-to-feedback-reset-${r.sid}`}
                                >
                                  {busySection === r.sid
                                    ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                    : <RotateCcw className="w-2.5 h-2.5" />}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => markFixed(r.sid, false)}
                                disabled={busySection === r.sid}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 hover:bg-amber-500/30 text-amber-700 dark:text-amber-300 border border-amber-500/30 disabled:opacity-50 transition-colors"
                                title="Mark this section as fixed — future feedback will be tagged POST-FIX"
                                data-testid={`admin-how-to-feedback-mark-${r.sid}`}
                              >
                                {busySection === r.sid
                                  ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                  : <Wrench className="w-2.5 h-2.5" />}
                                Mark fixed
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {(data?.recent_comments || []).length > 0 && (
                    <div className="mt-5">
                      <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent comments</h4>
                      <ul className="space-y-2">
                        {data.recent_comments.map((c, i) => {
                          const segments = parseCommentSegments(c.comment || '');
                          // Round 91f — surface "POST-FIX" badge when this
                          // comment was submitted AFTER the section's
                          // last_fixed_at stamp. Means the rewrite didn't
                          // fully land for that visitor — real signal.
                          const fixedAt = data?.last_fixed_map?.[c.section_id];
                          const isPostFix = !!(fixedAt && c.updated_at && c.updated_at > fixedAt);
                          return (
                            <li
                              key={i}
                              className={`text-xs border rounded p-2 bg-background ${isPostFix ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : ''}`}
                              data-testid={`admin-how-to-feedback-comment-${i}`}
                            >
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono font-bold text-violet-700 dark:text-violet-300">{SECTION_LABELS[c.section_id] || c.section_id}</span>
                                {c.rating === 'up'
                                  ? <ThumbsUp className="w-3 h-3 text-emerald-600" />
                                  : <ThumbsDown className="w-3 h-3 text-rose-600" />}
                                {isPostFix && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40"
                                    title={`Submitted after this section was marked fixed (${fixedAt?.slice(0, 10)})`}
                                    data-testid={`admin-how-to-feedback-comment-${i}-post-fix-badge`}
                                  >
                                    <CheckCircle2 className="w-2.5 h-2.5" /> Post-fix
                                  </span>
                                )}
                                <span className="text-muted-foreground ml-auto">{c.updated_at?.slice(0, 10)}</span>
                              </div>
                              <p className="text-foreground/80 leading-relaxed break-words">
                                {segments.map((seg, idx) => seg.type === 'ticker' ? (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                      if (typeof onStockClick === 'function') {
                                        setOpen(false);
                                        // Defer the modal-open so the close
                                        // animation doesn't fight the Stock
                                        // Modal mount.
                                        setTimeout(() => onStockClick(seg.value), 60);
                                      }
                                    }}
                                    className="inline-flex items-center font-mono font-bold text-[11px] px-1.5 py-0.5 mx-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 hover:bg-violet-200 dark:hover:bg-violet-900/60 transition-colors align-baseline"
                                    title={`Open ${seg.value} stock details`}
                                    data-testid={`admin-how-to-feedback-ticker-chip-${seg.value}-${i}`}
                                  >
                                    {seg.value}
                                  </button>
                                ) : (
                                  <span key={idx}>{seg.value}</span>
                                ))}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {grandTotal === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-6">No feedback yet — the How-To page hasn&apos;t collected any thumbs.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
